import { initialFiltersValue } from '@features/todos/utils/managerUtils'
import type {
  CalculatedTaskStatus,
  DashboardTaskGroup,
  RecurringTask,
  TaskItemResponse
} from '@lmc/ts-common'
import { theme } from '@lmc/ui-kit'
import { ListItem, StatusBadge } from '@lmc/ui-kit/src/dataDisplay'
import CollapsibleItem, {
  type ToggleCollapseFn
} from '@lmc/ui-kit/src/dataDisplay/CollapsibleItem/CollapsibleItem'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import { getJSON } from 'common/services/api'
import { getFormattedTimeDifference } from 'common/utils'
import { Spinner } from 'components/generic'
import dayjs from 'dayjs'
import { NAMESPACE } from 'locale'
import { toDosConstants } from 'locale/toDos'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import styled from 'styled-components'
import type { QueryResult } from 'types/UseQueryTypes'
import useTasks from '../hooks/useTasks'
import { convertPropsToParams } from '../utils/dashboardFilters'
import EditRecurrenceButton from './EditRecurrenceButton'
import Task from './Task'
import type { TaskFiltersValue } from './TaskFilters'
import TaskTags from './TaskTags'

type TaskGroupProps = {
  taskGroup: DashboardTaskGroup
  filterParams?: TaskFiltersValue
  refetchDailyTasks: () => Promise<void>
  testId?: string
}

const TaskGroup = ({
  taskGroup,
  filterParams,
  refetchDailyTasks,
  testId
}: TaskGroupProps) => {
  const { t } = useTranslation(NAMESPACE.TODOS)
  const [isTaskGroupCollapsed, setIsTaskGroupCollapsed] = useState(true)
  const [allowFetch, setAllowFetch] = useState(true)

  // this is the used variable for fetching
  const canFetchData = !isTaskGroupCollapsed && allowFetch
  const { isLoading, tasks, refetch } = useTasks(
    {
      recurringTaskID: taskGroup.recurringTaskID,
      ...convertPropsToParams(filterParams),
      from: taskGroup.dateTime,
      to: taskGroup.dateTime
    },
    {
      enabled: canFetchData,
      onSuccess: () => setAllowFetch(false)
    }
  )
  const { data: recurringTask } = useQuery<QueryResult<RecurringTask>>({
    queryKey: [taskGroup.recurringTaskID],
    queryFn: () => fetchReccuringTask(taskGroup.recurringTaskID),
    enabled: canFetchData
  })

  const onRefetch = async () => {
    setAllowFetch(true)
    await refetch()
  }
  const onRefetchDailyTasks = async () => {
    setAllowFetch(true)
    await refetchDailyTasks()
  }

  const formattedTimeDiff = getFormattedTimeDifference(taskGroup.dateTime)

  const areFiltersTouched =
    JSON.stringify(initialFiltersValue) !== JSON.stringify(filterParams)

  return (
    <CollapsibleToDo
      data-testid={testId || 'task-group'}
      header={(toggleCollapse: ToggleCollapseFn, collapsed: boolean) => (
        <StyledListItem
          onClick={toggleCollapse}
          data-testid={testId ? `${testId}-header` : 'task-group-header'}
          icon={
            <TaskGroupIcon
              color={taskGroup?.category?.color || theme.color.neutral.color8}
              data-testid={testId ? `${testId}-icon` : 'task-group-icon'}
            >
              <img
                alt="todo icon"
                src={taskGroup?.item?.icon?.url || taskGroup.category.icon}
                width={20}
                height={20}
              />
            </TaskGroupIcon>
          }
          title={
            <TaskTitleContainer
              data-testid={
                testId
                  ? `${testId}-title-container`
                  : 'task-group-title-container'
              }
            >
              <Timestamp
                level={5}
                data-testid={
                  testId ? `${testId}-timestamp` : 'task-group-timestamp'
                }
              >
                {dayjs(taskGroup.dateTime).format('HH:mm')}
              </Timestamp>
              <MobileTimestamp
                level={2}
                data-testid={
                  testId
                    ? `${testId}-mobile-timestamp`
                    : 'task-group-mobile-timestamp'
                }
              >
                {dayjs(taskGroup.dateTime).format('HH:mm')}
              </MobileTimestamp>
              <TaskTitle
                level={5}
                weight="medium"
                data-testid={testId ? `${testId}-title` : 'task-group-title'}
              >
                {taskGroup?.title}
              </TaskTitle>
            </TaskTitleContainer>
          }
          toggleCollapse={toggleCollapse}
          isCollapsed={collapsed}
          rightChildren={Object.entries(taskGroup.counts).map(
            ([statusType, count]) => (
              <StatusBadgeStyled
                key={statusType}
                status={statusType as CalculatedTaskStatus}
                count={count}
                data-testid={
                  testId
                    ? `${testId}-status-badge-${statusType}`
                    : `task-group-status-badge-${statusType}`
                }
              />
            )
          )}
        />
      )}
      onCollapseChange={(collapsed) => setIsTaskGroupCollapsed(collapsed)}
    >
      <ContentContainer
        data-testid={testId ? `${testId}-content` : 'task-group-content'}
      >
        <CategoryText
          data-testid={testId ? `${testId}-category` : 'task-group-category'}
        >
          {' '}
          {taskGroup?.category?.name}
        </CategoryText>
        <DescriptionText
          level={5}
          data-testid={
            testId ? `${testId}-description` : 'task-group-description'
          }
        >
          {taskGroup?.description || ''}
        </DescriptionText>
        <OtherInfo
          data-testid={
            testId ? `${testId}-other-info` : 'task-group-other-info'
          }
        >
          {(taskGroup?.counts?.pending || taskGroup?.counts?.due) && (
            <TimeRemaining
              data-testid={
                testId
                  ? `${testId}-time-remaining`
                  : 'task-group-time-remaining'
              }
            >
              <Text weight="bold">{formattedTimeDiff}</Text>
              <Text>
                {taskGroup?.counts?.pending
                  ? t(toDosConstants.UNTIL_DUE)
                  : t(toDosConstants.UNTIL_OVERDUE)}
              </Text>
            </TimeRemaining>
          )}
          <TaskTags
            recurrence={recurringTask?.result?.recurrence}
            taskType={taskGroup.taskType}
            doubleSignature={taskGroup.doubleSignature}
            logScreenPrompt={taskGroup.logScreenPrompt}
            data-testid={
              testId ? `${testId}-task-tags` : 'task-group-task-tags'
            }
          />
        </OtherInfo>
        <TasksContainer
          data-testid={
            testId ? `${testId}-tasks-container` : 'task-group-tasks-container'
          }
        >
          {isLoading ? (
            <Spinner
              data-testid={
                testId
                  ? `${testId}-loading-spinner`
                  : 'task-group-loading-spinner'
              }
            />
          ) : (
            tasks?.map((task: TaskItemResponse, index) => (
              <Task
                key={task.id}
                task={task}
                refetchDailyTasks={onRefetchDailyTasks}
                refetchTasks={onRefetch}
                data-testid={
                  testId
                    ? `${testId}-task-${index}`
                    : `task-group-task-${index}`
                }
              />
            ))
          )}
          {areFiltersTouched && (
            <AppliedFiltersText
              data-testid={
                testId
                  ? `${testId}-applied-filters`
                  : 'task-group-applied-filters'
              }
            >
              <StyledHeading level={5}>
                {t(toDosConstants.FILTERS_SHOWING)}
                <TaskCount>{tasks?.length}</TaskCount>
                {t(toDosConstants.FILTERS_APPLIED)}
              </StyledHeading>
            </AppliedFiltersText>
          )}
          {!isTaskGroupCollapsed && (
            <EditRecurrenceButton
              recurringTaskID={taskGroup.recurringTaskID}
              data-testid={
                testId
                  ? `${testId}-edit-recurrence-button`
                  : 'task-group-edit-recurrence-button'
              }
            />
          )}
        </TasksContainer>
      </ContentContainer>
    </CollapsibleToDo>
  )
}

const fetchReccuringTask = (id: string) =>
  getJSON({ url: `/api/list/recurring-tasks/${id}` })

const ContentContainer = styled.div`
  padding: 8px 144px 46px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    padding: 16px;
  }
`

const CategoryText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

const DescriptionText = styled(Heading)`
  display: block;
  color: ${({ theme }) => theme.color.neutral.color3};
`

const OtherInfo = styled.div`
  display: flex;
  align-items: center;
  margin: 24px 0;
`
const TimeRemaining = styled(Text)`
  line-height: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-right: 8px;
`
const TasksContainer = styled.div``

const AppliedFiltersText = styled.div`
  margin-top: 12px;
  padding: 0 16px;
  color: ${({ theme }) => theme.color.neutral.color5};
`

const TaskCount = styled.div`
  font-weight: 700;
`

const StyledHeading = styled(Heading)`
  display: flex;
  gap: 4px;
`

const CollapsibleToDo = styled(CollapsibleItem)`
  border: 1px solid ${({ theme }) => theme.color.neutral.color8};
  border-radius: 16px;
`

const TaskGroupIcon = styled.div<{ color: string }>`
  width: 40px;
  height: 40px;
  border-radius: 9px;
  background-color: ${(props) => props.color};
  padding: 10px;
  display: flex;
  align-items: center;
  vertical-align: center;
`

const StyledListItem = styled(ListItem)`
  background-color: ${({ theme }) => theme.color.neutral.color11};
  cursor: pointer;
`

const TaskTitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
  }
`

const Timestamp = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color4};
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    display: none;
  }
`

const MobileTimestamp = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
  display: none;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    display: block;
  }
`

const StatusBadgeStyled = styled(StatusBadge)`
  padding: 4px 8px;
`

const TaskTitle = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
`

export default TaskGroup
